import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { expenseService, tripService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useCapture } from '../context/CaptureContext';
import { runOcrOnImage } from '../utils/receiptOcr';
import { findTripForDate } from '../utils/trips';
import { enqueueExpense, isOfflineError, newIdempotencyKey, syncPending } from '../services/offlineQueue';

const today = () => new Date().toISOString().split('T')[0];

/**
 * State and behaviour shared by the desktop and mobile expense forms:
 * loading/saving, receipt file + OCR, EUR conversion, trip auto-linking
 * and offline queueing of new expenses.
 *
 * autoApplyOcr: fill untouched fields straight from OCR (mobile) instead of
 * only offering suggestion chips (desktop).
 */
function useExpenseForm({ id, autoApplyOcr = false }) {
  const isEditing = Boolean(id);
  const { user } = useAuth();
  const { takePendingCapture, openScanner } = useCapture();
  const [searchParams, setSearchParams] = useSearchParams();
  const isOcrEnabled = user?.ocr_enabled !== false; // default true

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fileType, setFileType] = useState(null); // 'image' or 'pdf'
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditing);
  const [loadError, setLoadError] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrSuggestions, setOcrSuggestions] = useState(null); // { amount, date, merchant }
  const [ocrFilled, setOcrFilled] = useState([]); // fields auto-filled from OCR

  const [formData, setFormData] = useState({
    date: today(),
    amount: '',
    currency: 'EUR',
    description: '',
    merchant: '',
    category: '',
    attendees: '',
    client: '',
    trip_id: '',
  });
  // Mirror of formData readable from async callbacks (OCR)
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  // Fields the user edited by hand — OCR and trip auto-linking leave them alone
  const touchedRef = useRef(new Set(isEditing ? ['trip_id'] : []));
  const [autoLinkedTrip, setAutoLinkedTrip] = useState(false);
  const idempotencyKeyRef = useRef(newIdempotencyKey());

  const [suggestions, setSuggestions] = useState({ clients: [], attendees: [], merchants: [] });
  const [trips, setTrips] = useState([]);
  const [convertedAmount, setConvertedAmount] = useState(null);
  const [conversionRate, setConversionRate] = useState(null);

  // Fetch suggestions and trips on mount
  useEffect(() => {
    expenseService.getFieldSuggestions()
      .then(setSuggestions)
      .catch(err => console.log('Could not fetch suggestions:', err));
    tripService.getTrips()
      .then(setTrips)
      .catch(err => console.log('Could not fetch trips:', err));
  }, []);

  // Fetch existing expense when editing
  useEffect(() => {
    if (!isEditing) return;
    expenseService.getExpense(id)
      .then(expense => {
        setFormData({
          date: expense.date ? expense.date.split('T')[0] : today(),
          amount: expense.amount?.toString() || '',
          currency: expense.currency || 'EUR',
          description: expense.description || '',
          merchant: expense.merchant || '',
          category: expense.category || '',
          attendees: expense.attendees || '',
          client: expense.client || '',
          trip_id: expense.trip_id ? String(expense.trip_id) : '',
        });
        if (expense.image_url) {
          const isPdf = expense.image_url.toLowerCase().endsWith('.pdf');
          setFileType(isPdf ? 'pdf' : 'image');
          setExistingImageUrl(expense.image_url);
          setPreview(expense.image_url);
        }
      })
      .catch(error => {
        console.error('Error fetching expense:', error);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [id, isEditing]);

  // Calculate EUR conversion when amount or currency changes
  useEffect(() => {
    if (formData.currency === 'EUR' || !formData.amount || parseFloat(formData.amount) <= 0) {
      setConvertedAmount(null);
      setConversionRate(null);
      return;
    }
    let cancelled = false;
    expenseService.convertCurrency(parseFloat(formData.amount), formData.currency, 'EUR')
      .then(result => {
        if (!cancelled && result.converted_amount) {
          setConvertedAmount(result.converted_amount);
          setConversionRate(result.conversion_rate);
        }
      })
      .catch(error => {
        console.log('Could not fetch conversion rate:', error);
        if (!cancelled) {
          setConvertedAmount(null);
          setConversionRate(null);
        }
      });
    return () => { cancelled = true; };
  }, [formData.amount, formData.currency]);

  // Link the expense to the trip covering its date, unless the user picked one
  useEffect(() => {
    if (touchedRef.current.has('trip_id')) return;
    const match = findTripForDate(trips, formData.date);
    setFormData(prev => ({ ...prev, trip_id: match ? String(match.id) : '' }));
    setAutoLinkedTrip(Boolean(match));
  }, [formData.date, trips]);

  const applyOcr = useCallback((result) => {
    if (!autoApplyOcr) {
      setOcrSuggestions(result);
      return;
    }
    const current = formDataRef.current;
    const touched = touchedRef.current;
    const updates = {};
    if (result.amount && !touched.has('amount') && !current.amount) updates.amount = result.amount;
    if (result.date && !touched.has('date')) updates.date = result.date;
    if (result.merchant && !touched.has('merchant') && !current.merchant) updates.merchant = result.merchant;
    setFormData(prev => ({ ...prev, ...updates }));
    setOcrFilled(Object.keys(updates));
  }, [autoApplyOcr]);

  const processFile = useCallback(async (selectedFile, previewUrl = null) => {
    setFile(selectedFile);
    setOcrSuggestions(null);
    setOcrFilled([]);

    const isPdf = selectedFile.type === 'application/pdf' || selectedFile.name.toLowerCase().endsWith('.pdf');
    setFileType(isPdf ? 'pdf' : 'image');
    setPreview(previewUrl || URL.createObjectURL(selectedFile));

    if (!isPdf && isOcrEnabled) {
      setOcrLoading(true);
      setOcrProgress(0);
      try {
        const result = await runOcrOnImage(selectedFile, setOcrProgress);
        if (result.amount || result.date || result.merchant) applyOcr(result);
      } catch (err) {
        console.warn('OCR failed:', err);
      } finally {
        setOcrLoading(false);
      }
    }
  }, [isOcrEnabled, applyOcr]);

  // Pick up a receipt captured from the bottom-nav ＋ sheet, or open the
  // scanner straight away for the "Scan receipt" home-screen shortcut (?scan=1)
  useEffect(() => {
    if (isEditing) return;
    const pending = takePendingCapture();
    if (pending) {
      processFile(pending.file, pending.previewUrl);
    } else if (searchParams.get('scan') === '1') {
      setSearchParams({}, { replace: true });
      openScanner((capturedFile, previewUrl) => processFile(capturedFile, previewUrl));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = useCallback((name, value) => {
    touchedRef.current.add(name);
    if (name === 'trip_id') setAutoLinkedTrip(false);
    setOcrFilled(prev => prev.filter(f => f !== name));
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleChange = useCallback((e) => setField(e.target.name, e.target.value), [setField]);

  // Apply OCR suggestion chips (desktop)
  const applySuggestions = useCallback((fields) => {
    setFormData(prev => ({ ...prev, ...fields }));
    setOcrSuggestions(prev => {
      if (!prev) return prev;
      const next = { ...prev };
      Object.keys(fields).forEach(k => { next[k] = null; });
      return next.amount || next.date || next.merchant ? next : null;
    });
  }, []);

  const clearFile = useCallback(() => {
    setFile(null);
    setOcrFilled([]);
    // If editing, restore existing image preview
    if (isEditing && existingImageUrl) {
      setFileType(existingImageUrl.toLowerCase().endsWith('.pdf') ? 'pdf' : 'image');
      setPreview(existingImageUrl);
    } else {
      setFileType(null);
      setPreview(null);
    }
  }, [isEditing, existingImageUrl]);

  /**
   * Validate and save. Resolves to:
   *   { status: 'saved' } | { status: 'queued' } | { status: 'invalid' | 'error', message }
   * New expenses are queued for later upload when there is no connection.
   */
  const submit = useCallback(async () => {
    if (!isEditing && !file) return { status: 'invalid', message: 'Add a photo or PDF of the receipt' };
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      return { status: 'invalid', message: 'Enter a valid amount' };
    }

    setSaving(true);
    try {
      if (isEditing) {
        await expenseService.updateExpense(id, formData);
        return { status: 'saved' };
      }
      if (!navigator.onLine) {
        await enqueueExpense(formData, file, idempotencyKeyRef.current);
        return { status: 'queued' };
      }
      await expenseService.createExpense(formData, file, idempotencyKeyRef.current);
      // Good moment to flush anything captured earlier while offline
      syncPending();
      return { status: 'saved' };
    } catch (error) {
      if (!isEditing && isOfflineError(error)) {
        await enqueueExpense(formData, file, idempotencyKeyRef.current);
        return { status: 'queued' };
      }
      console.error('Save error:', error);
      if (isEditing && isOfflineError(error)) {
        return { status: 'error', message: "You're offline — editing needs a connection" };
      }
      return { status: 'error', message: 'Error saving expense' };
    } finally {
      setSaving(false);
    }
  }, [isEditing, id, file, formData]);

  return {
    isEditing,
    loading,
    loadError,
    saving,
    file,
    preview,
    fileType,
    existingImageUrl,
    ocrLoading,
    ocrProgress,
    ocrSuggestions,
    setOcrSuggestions,
    ocrFilled,
    formData,
    setField,
    handleChange,
    applySuggestions,
    suggestions,
    trips,
    autoLinkedTrip,
    convertedAmount,
    conversionRate,
    processFile,
    clearFile,
    submit,
  };
}

export default useExpenseForm;
